import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Lightbulb, Handshake, Globe, CheckCircle2, ShieldCheck, Target } from "lucide-react";

const About = () => {
  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>About Us | Sooners Land LLC</title>
        <meta name="description" content="Learn about Sooners Land LLC, our mission, values, and dedication to engineering excellence and sustainable solutions." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-primary pt-32 pb-24 px-6 md:px-12 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full skew-y-6 bg-accent/5 origin-top-left" />
        <div className="container mx-auto text-center relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Our Story & <span className="text-accent italic">Vision</span>
          </motion.h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            We are a dedicated engineering consultant services company committed to delivering innovative and sustainable solutions with excellence across the globe.
          </p>
        </div>
      </section>

      {/* Mission & Values Grid */}
      <section className="section-padding -mt-12">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 md:p-14 rounded-[2.5rem] shadow-xl border border-slate-100 flex flex-col justify-center"
            >
              <div className="w-16 h-16 bg-accent-soft rounded-2xl flex items-center justify-center mb-8">
                <Target className="text-accent" size={32} />
              </div>
              <h2 className="text-3xl font-bold mb-6 text-primary">Our Mission</h2>
              <p className="text-slate-700 text-lg leading-relaxed mb-6 font-medium">
                To combine creativity, cutting-edge technology, and unparalleled expertise to build a better future for communities and industries worldwide.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                We strive to be the bridge between complex engineering challenges and elegant, functional solutions that stand the test of time.
              </p>
            </motion.div>

            {/* Values */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-primary p-10 md:p-14 rounded-[2.5rem] shadow-2xl text-white"
            >
              <h2 className="text-3xl font-bold mb-10 text-white">Core Values</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {[
                  { icon: <Handshake className="text-accent" />, title: "Integrity", desc: "Honesty and transparency in every partnership." },
                  { icon: <Globe className="text-accent" />, title: "Sustainability", desc: "Green solutions for a lasting impact." },
                  { icon: <Lightbulb className="text-accent" />, title: "Innovation", desc: "Pushing the boundaries of engineering." },
                  { icon: <ShieldCheck className="text-accent" />, title: "Excellence", desc: "Highest standards in delivery and safety." }
                ].map((value, i) => (
                  <div key={i} className="flex flex-col gap-3">
                    <div className="mb-2">{value.icon}</div>
                    <h4 className="text-xl font-bold text-white tracking-tight">{value.title}</h4>
                    <p className="text-slate-200 text-sm leading-relaxed font-medium">{value.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="section-padding bg-slate-100">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <h2 className="text-base font-bold text-accent uppercase tracking-widest mb-4">Our Approach</h2>
              <h3 className="text-3xl md:text-4xl font-bold mb-6 text-primary">Engineering with a Global Perspective</h3>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Our team brings together decades of experience from major projects in the USA, Saudi Arabia, Qatar, and beyond. This diverse background allows us to tackle infrastructure challenges with a unique, multi-faceted approach.
              </p>
              <div className="flex flex-col gap-4">
                {["Rigorous Quality Control", "Strategic Project Management", "Advanced Tech Integration"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 font-semibold text-primary">
                    <CheckCircle2 size={20} className="text-accent" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="md:w-1/2 grid grid-cols-2 gap-4">
              <div className="aspect-square bg-white rounded-3xl p-8 flex flex-col justify-center items-center text-center shadow-lg border border-slate-200">
                <span className="text-4xl font-black text-accent mb-2">100+</span>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Projects Delivered</span>
              </div>
              <div className="aspect-square bg-primary rounded-3xl p-8 flex flex-col justify-center items-center text-center shadow-lg mt-8">
                <span className="text-4xl font-black text-white mb-2">Tier-1</span>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Design Partners</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container mx-auto text-center px-6">
          <div className="bg-accent rounded-[3rem] p-12 md:p-20 text-white shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:scale-110 transition-transform duration-700" />
            <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">Ready to Build the Future?</h2>
            <p className="text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed mb-10 relative z-10 opacity-90">
              Contact us today to discuss your engineering needs and let’s build something amazing together.
            </p>
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-white text-accent px-10 py-4 rounded-full font-bold shadow-xl hover:bg-slate-50 transition-colors relative z-10"
            >
              Get in Touch
            </motion.a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
